<template>
    <div class="library">
        <Header
            :sort="queryParams.sort"
            @sort="s => queryParams.sort = s"
            :layout="layout"
            @layout="(l: 'grid' | 'list') => layout = l"
            :amount="mediaCount"
            @selecting="select => selecting = select"
            @selectAllPage="selectAllPage => selectAllOnPage = selectAllPage"
        ></Header>

        <div class="container">
            <div class="row">
                <div class="col-sm-12 col-md-3">
                    <div class="mb-2">&nbsp;</div>
                    <div class="form-group">
                        <label for="search">{{ $t('site.filters.search') }}</label>
                        <input class="form-control" v-model="queryParams['filter[name]']" id="search" :disabled="loading" @change="queryParams.page = 1;" />
                    </div>
                    <div class="form-group" v-if="isRendered">
                        <label for="categories">{{ $t('site.filters.categories') }}</label>
                        <Multiselect
                            v-model="queryParams['filter[categories.id]']"
                            @change="queryParams.page = 1;"
                            :filter-results="false"
                            :min-chars="1"
                            mode="tags"
                            :resolve-on-load="true"
                            :delay="200"
                            :searchable="true"
                            :object="true"
                            :disabled="loading"
                            valueProp="id"
                            trackBy="name"
                            :placeholder="$t('site.filters.search')"
                            label="name"
                            :options="fetchCategories"
                        ></Multiselect>
                    </div>
                    <div class="form-group" v-if="isRendered">
                        <label for="tags">{{ $t('site.filters.tags') }}</label>
                        <Multiselect
                            v-model="queryParams['filter[tags.id]']"
                            @change="queryParams.page = 1;"
                            :filter-results="false"
                            :min-chars="1"
                            mode="tags"
                            :resolve-on-load="true"
                            :delay="200"
                            :searchable="true"
                            :object="true"
                            :disabled="loading"
                            valueProp="id"
                            trackBy="name"
                            label="name"
                            :placeholder="$t('site.filters.search')"
                            :options="fetchTags"
                        ></Multiselect>
                    </div>
                    <div class="form-group" v-if="isRendered">
                        <label for="creator">{{ $t('site.filters.creator') }}</label>
                        <Multiselect
                            v-model="queryParams['filter[contract.creator_id]']"
                            @change="queryParams.page = 1;"
                            :filter-results="false"
                            :min-chars="1"
                            mode="tags"
                            :resolve-on-load="true"
                            :delay="200"
                            :searchable="true"
                            :object="true"
                            :disabled="loading"
                            valueProp="id"
                            trackBy="alias"
                            label="alias"
                            :placeholder="$t('site.filters.search')"
                            :options="fetchCreators"
                        ></Multiselect>
                    </div>
                    <div class="form-group">
                        <label for="quality">{{ $t('site.filters.quality') }}</label>
                        <select class="form-select" id="quality" v-model="queryParams['filter[mux_signed_data->max_stored_resolution]']" :disabled="loading" @change="queryParams.page = 1;">
                            <option :value="null">{{ $t('site.filters.all') }}</option>
                            <option value="SD">SD</option>
                            <option value="HD">HD</option>
                            <option value="UHD">UHD</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label for="quality">{{ $t('site.filters.orientation') }}</label>
                        <select class="form-select" id="quality" v-model="queryParams['filter[orientation]']" :disabled="loading" @change="queryParams.page = 1;">
                            <option :value="null">{{ $t('site.filters.all') }}</option>
                            <option value="portrait">Portrait</option>
                            <option value="landscape">Landscape</option>
                            <option value="square">Square</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label for="creator">{{ $t('site.filters.duration') }}</label>
                        <Slider v-model="duration" tooltipPosition="bottom" :format="formatTooltip" class="slider mt-3" :min="0" :max="3600" :disabled="loading"></Slider>
                        <div class="d-flex justify-space-between mt-3">
                            <input class="form-control me-2" id="duration.min" :value="formatTooltip(duration[0])" @change="setValue(0, $event)" :disabled="loading"  />
                            <input class="form-control ms-2" id="duration.max" :value="formatTooltip(duration[1])" @change="setValue(1, $event)" :disabled="loading" />
                        </div>
                    </div>
                    <button class="btn btn-link px-0 mb-3 d-flex align-items-center" @click="resetFilters">
                        {{ $t('site.actions.reset_all_filters') }}
                    </button>
                </div>
                <div v-if="mediaData !== null" class="col-sm-12 col-md-9">
                    <div class="mb-2">&nbsp;</div>

                    <div v-if="loading" class="progress mb-3" role="progressbar" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100">
                        <div class="progress-bar progress-bar-striped progress-bar-animated" style="width: 100%">
                            {{ $t('site.loading') }}...
                        </div>
                    </div>

                        <keep-alive>
                            <LibraryGrid v-if="layout === 'grid'" :selecting="selecting" :items="mediaData.data"></LibraryGrid>
                            <LibraryList v-else-if="layout === 'list'" :selecting="selecting" :items="mediaData.data" :selected="selected" @selected="s => selected = s"></LibraryList>
                        </keep-alive>
                    <div class="d-flex flex-column flex-md-row align-items-center">
                        <p>
                            {{ $t('site.pagination.showing_pages', {currentPage: queryParams['page'], totalPages: Math.round(mediaCount / 30)}) }}
                        </p>
                        <div class="ms-md-auto pagination mb-3">
                            <a class="btn btn-primary" style="border-bottom-right-radius: 0;border-top-right-radius: 0" href="#!" @click="queryParams['page'] = queryParams['page'] - 1; scrollToTop();">{{ $t('site.pagination.previous') }}</a>
                            <input class="form-control form-control-sm flex-shrink-1 rounded-0 page-input" type="number" min="1" @change="scrollToTop()" v-model="queryParams['page']">
                            <a class="btn btn-primary" style="border-bottom-left-radius: 0;border-top-left-radius: 0" href="#!" @click="queryParams['page'] = queryParams['page'] + 1; scrollToTop();">{{ $t('site.pagination.next') }}</a>
                        </div>
                    </div>
                </div>
                <div v-else class="col-sm-12 col-md-9">
                    <div class="row mt-4">
                        <div v-for="index in 30" :key="index" class="col-6 col-md-4">
                            <div class="video-placeholder mb-4">
                                <div class="video-element">
                                    <div class="video bg-light mb-2" style="aspect-ratio: 16/9; object-fit: cover"></div>
                                </div>
                                <p class="placeholder-glow">
                                    <span class="placeholder bg-primary w-50 me-2"></span> <span class="placeholder bg-primary w-25"></span> <span class="placeholder bg-primary w-75"></span>
                                    <span class="placeholder w-50"></span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import Header from "@/site/components/Header.vue";
import {nextTick, onMounted, reactive, ref, watch, provide} from "vue";
import {cloneDeep, debounce, isNil, uniq} from "lodash";
import Pagination from "@/site/components/Pagination.vue";
import Slider from '@vueform/slider';
import Multiselect from "@vueform/multiselect";
import LibraryGrid from "@/site/components/LibraryGrid.vue";
import LazyLoad from "vanilla-lazyload";
import LibraryList from "@/site/components/LibraryList.vue";
import Recycle from "@/site/components/Icons/Recycle.vue";
import Media from "@/pcm/store/models/Media";

const lazyLoadInstance = new LazyLoad();
const layout = ref<'grid' | 'list'>('grid');
const loading = ref<boolean>(false);
const duration = ref<number[]>([
    0, 3600,
]);
const selecting = ref<boolean>(false);
const selectAllOnPage = ref<boolean>(false);
const isRendered = ref<boolean>(false);
const selected = ref<Array<number>>([]);
const mediaCount = ref<number>(0);

function toggleMedia(mediaId: number): void {
    if (selected.value?.includes(mediaId)){
        selected.value.splice(selected.value.indexOf(mediaId), 1);
    } else {
        selected.value?.push(mediaId);
    }
}

provide('selected', {
    selected,
    toggleMedia,
});

const urlParams = new URLSearchParams(window.location.search);

const queryParams = reactive({
    'page': 1,

    sort: '-id',
    include: 'contract.creator,tags',
    append: 'thumbnail_url,thumbnail_gif_url',

    'filter[name]': null,
    'filter[categories.id]': [],
    'filter[mux_signed_data->max_stored_resolution]': null,
    'filter[orientation]': null,
    'filter[tags.id]': [],
    'filter[contract.creator_id]': [],
    'filter[duration.min]': null as number | null,
    'filter[duration.max]': null as number | null,
});

const originalQueryParams = cloneDeep(queryParams);
const mediaData = ref<any>(null);
let skipWatcher = false;
let getQueryParams = false;

queryParams["filter[name]"] = urlParams.get('filter[search]') ?? queryParams["filter[name]"];

const fetchData = async () => {
    loading.value = true;

    if (!isNil(urlParams.get('filter[categories]')) && !getQueryParams) {
        await window.api.get('categories', {params: {'filter[name]': urlParams.get('filter[categories]')}})
            .then(response => {
                skipWatcher = true;
                getQueryParams = true;
                queryParams['filter[categories.id]'] = response.data.data;
            });
    }

    if (!isNil(urlParams.get('filter[tags]')) && !getQueryParams) {
        await window.api.get('tags', {params: {'filter[id]': urlParams.get('filter[tags]')}})
            .then(response => {
                skipWatcher = true;
                getQueryParams = true;
                queryParams['filter[tags.id]'] = response.data.data;
            });
    }

    const query = cloneDeep(queryParams);
    query['filter[categories.id]'] = queryParams['filter[categories.id]'].map((i: any) => i.id) as any;
    query['filter[tags.id]'] = queryParams['filter[tags.id]'].map((i: any) => i.id) as any;
    query['filter[contract.creator_id]'] = queryParams['filter[contract.creator_id]'].map((i: any) => i.id) as any;

    window.api.get('media', {
        params: query,
    })
        .then(response => {
            mediaData.value = response.data;

            nextTick(() => {
                lazyLoadInstance.loadAll();
            });
        })
        .finally(() => {
            loading.value = false;
            skipWatcher = false;
        });

    getMediaCount(query);
}

const getMediaCount = (query: any) => {
    mediaCount.value = null;
    window.api.get('media/count', {
        params: query,
    })
        .then(response => {
            mediaCount.value = response.data.total;
        });
}

const formatTooltip = (value: number | null) => {
    return window.secondsToHumanReadable(value);
}

const setValue = (index: number, event: any) => {
    queryParams.page = 1;

    duration.value[index] = window.humanReadableToSeconds(event.target.value);
}

const fetchCategories = async (query: string) => {
    const response = await window.api.get('categories', {
        params: {
            'filter[name]': query,
        }
    });

    return response.data.data;
}

const fetchTags = async (query: string) => {
    const response = await window.api.get('tags', {
        params: {
            'filter[name]': query,
        }
    });

    return response.data.data;
}

const fetchCreators = async (query: string) => {
    const response = await window.api.get('creators', {
        params: {
            'filter[search]': query,
        }
    });

    return response.data.data;
}

watch(() => queryParams, debounce(() => {
    if (!skipWatcher) {
        fetchData();
    }
}, 700), {deep: true});

fetchData();

watch(() => duration.value, (duration) => {
   queryParams['filter[duration.min]'] = duration[0];
   queryParams['filter[duration.max]'] = duration[1];
}, { deep: true });

watch(() => selectAllOnPage.value, (select) => {
    if (select) {
        selected.value = uniq(selected.value.concat(mediaData.value.data.map((item: Media) => item.id)));
    }
});

const canBeRendered = (tries = 0) => {
    if (tries >= 10) {
        return;
    }

    if (isNil(window.api)) {
        return setTimeout(() => {
            canBeRendered(tries + 1);
        }, 500);
    }

    isRendered.value = true;
}

const resetFilters = () => {
    for (const key in originalQueryParams) {
        queryParams[key] = originalQueryParams[key];
    }

    duration.value[0] = 0;
    duration.value[1] = 3600;

    nextTick(() => {
        (document.getElementById('duration.min') as HTMLInputElement).value = duration.value[0] as any;
        (document.getElementById('duration.max') as HTMLInputElement).value = duration.value[1] as any;
    });
}

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
}

onMounted(() => {
    document.querySelectorAll('.slider-tooltip').forEach(el => {
        el.remove();
    });

    canBeRendered();
});
</script>
